import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import IconButtonWhiteBg from '../../../common/IconButtonWhiteBg';
import SelectTrainingSet from '../../../common/SelectTrainingSet';
import SvgIconCopy from '../../../../images/icons/Glynt_icon_Copy.svg?react';
import useGlynt from '../../../hooks/useGlynt';
import { AppContext } from '../../../../App';
import { AxiosError } from 'axios';
import { ITrainingSet } from '../../../../types/my-types';
import { ToolActionsContext } from '../../../../context/ToolActionsContext';
import { getIdFromUrl } from '../../../../utils/getIdFromUrl';
import { useParams } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';

interface CopyToTrainProps {
  isIcon?: boolean;
}

const CopyToTrain: React.FC<CopyToTrainProps> = ({ isIcon }) => {
  const { dataPoolId, setToast } = React.useContext(AppContext);
  const { setOpen, isReview } = React.useContext(ToolActionsContext);
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [trainingSet, setTrainingSet] = React.useState<ITrainingSet>();
  const { eid } = useParams<{ eid: string }>();

  if (!eid) {
    throw new Error('eid is not defined');
  }

  const glynt = useGlynt();

  const { isPending, isError, data } = useQuery({
    queryKey: ['extraction', eid, { view: 'corrected' }],
    queryFn: async () => (await glynt).extraction.retrieve_corrected_results(eid),
    enabled: isReview && dialogOpen,
    refetchOnWindowFocus: false,

    // 1 hour
    staleTime: 1000 * 60 * 60 * 1,

    // 1 hour
    gcTime: 1000 * 60 * 60 * 1
  });
  // const [copyDoc] = useMutation(COPY_EXTRACTION_TO_TRAIN);
  const copyDoc = useMutation({
    mutationFn: async (trainingSetId: string) => (await glynt).extraction.copy(eid, trainingSetId),

    onSuccess: () => {
      setToast({
        open: true,
        message: 'Done!',
        variant: 'success',
      });
      setLoading(false);
      setDialogOpen(false);
    },

    onError: async (error: AxiosError) => {
      setToast({
        open: true,
        message: error.message || 'There was an error. Please try again',
        variant: 'error',
      });
      setLoading(false);
    }
  });

  if (!isReview) {
    return null;
  }

  if (isPending || isError) return null;
  const currentTrainingSetId = getIdFromUrl(data?.training_set);

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleOk = async () => {
    setLoading(true);
    await copyDoc.mutate(trainingSet?.id || currentTrainingSetId);
  };

  return (
    <>
      {isIcon ? (
        <IconButtonWhiteBg
          disabled={false}
          onClick={() => {
            setDialogOpen(true);
            setOpen(false);
          }}
          id="icon-copy-to-train"
          cypressId="icon-copy-to-train"
          tooltip="Copy to Train"
          sx={{ '& svg': { color: 'black' }, '& svg path': { fill: 'black', stroke: 'black' } }}
        >
          <SvgIconCopy style={{ width: '14px', height: '14px' }} />
        </IconButtonWhiteBg>
      ) : (
        <MenuItem
          onClick={() => {
            setDialogOpen(true);
            setOpen(false);
          }}
        >
          Copy to train
        </MenuItem>
      )}

      <Dialog
        fullWidth
        maxWidth="md"
        open={dialogOpen}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Copy data to training</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Please select a training set.
          </DialogContentText>
          <SelectTrainingSet
            style={{ width: '100%' }}
            setTrainingSet={setTrainingSet}
            dataPoolId={dataPoolId}
            currentTrainingSetId={currentTrainingSetId}
            includeCurrentTS={true}
          />
        </DialogContent>

        {loading ? (
          <LinearProgress />
        ) : (
          <DialogActions>
            <Button id="common-alert-dialog-cancel" onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button id="common-alert-dialog-ok" onClick={handleOk} color="primary">
              Submit
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default CopyToTrain;
